import React, { useEffect, useState } from 'react';
import './DungeonManager.scss';

interface LootContainer {
  refrId: number;
  entryCount: number;
}

interface DungeonSummary {
  id: string;
  name: string;
  cellOrWorldDesc: string[];
  excludedCount: number;
  zonesCount: number;
  sessionTimeLimitMinutes: number;
  resetDelayMinutes: number;
  phase: 'idle' | 'active' | 'resetting';
  lootContainers: LootContainer[];
}

interface ShowEventDetail {
  dungeons: DungeonSummary[];
}

const PHASE_LABEL: Record<string, string> = {
  idle: 'Au repos',
  active: 'Active',
  resetting: 'Reset en cours',
};

const send = (payload: Record<string, unknown>): void => {
  if ((window as any).skyrimPlatform?.sendMessage) {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'dungeon-manager', ...payload }));
  }
};

const DungeonManager: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [dungeons, setDungeons] = useState<DungeonSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [sessionDraft, setSessionDraft] = useState('');
  const [resetDraft, setResetDraft] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      const list = detail.dungeons ?? [];
      setDungeons(list);
      setVisible(true);
      setSelectedId((prev) => (prev && list.some((d) => d.id === prev) ? prev : list[0]?.id ?? null));
    };
    window.addEventListener('skymp5-client:showDungeonManager', handler);
    return () => window.removeEventListener('skymp5-client:showDungeonManager', handler);
  }, []);

  const selected = dungeons.find((d) => d.id === selectedId) ?? null;

  useEffect(() => {
    if (!selected) return;
    setNameDraft(selected.name);
    setSessionDraft(String(Math.round(selected.sessionTimeLimitMinutes)));
    setResetDraft(String(Math.round(selected.resetDelayMinutes)));
    setConfirmingDelete(false);
  }, [selectedId, selected?.name, selected?.sessionTimeLimitMinutes, selected?.resetDelayMinutes]);

  const close = () => {
    setVisible(false);
    send({ action: 'close' });
  };

  if (!visible) return null;

  return (
    <div className="dungeon-manager-overlay">
      <div className="dungeon-manager-shell">

        <div className="dungeon-manager-header">
          <div className="dungeon-manager-header__row">
            <div className="dungeon-manager-header__text">
              <div className="dungeon-manager-header__brand">Administration</div>
              <h2 className="dungeon-manager-header__title">Gestionnaire de donjons</h2>
            </div>
          </div>
          <div className="dungeon-manager-header__buttons">
            <button type="button" className="dungeon-manager-btn dungeon-manager-btn--secondary" onClick={() => send({ action: 'refresh' })}>
              Rafraîchir
            </button>
            <button type="button" className="dungeon-manager-btn dungeon-manager-btn--secondary" onClick={close}>
              Fermer
            </button>
          </div>
        </div>

        <div className="dungeon-manager-body">
          <div className="dungeon-manager-list">
            {dungeons.length === 0 && <div className="dungeon-manager-empty">Aucun donjon configuré.</div>}
            {dungeons.map((d) => (
              <button
                type="button"
                key={d.id}
                className={'dungeon-manager-list-item' + (d.id === selectedId ? ' dungeon-manager-list-item--selected' : '')}
                onClick={() => setSelectedId(d.id)}
              >
                <span className="dungeon-manager-list-item__name">{d.name}</span>
                <span className={`dungeon-manager-phase-dot dungeon-manager-phase-dot--${d.phase}`} title={PHASE_LABEL[d.phase]} />
              </button>
            ))}
          </div>

          <div className="dungeon-manager-detail">
            {!selected && <div className="dungeon-manager-empty">Sélectionne un donjon dans la liste.</div>}

            {selected && (
              <>
                <div className="dungeon-manager-section">
                  <label className="dungeon-manager-section__heading">Nom</label>
                  <div className="dungeon-manager-inline">
                    <input
                      className="dungeon-manager-input"
                      type="text"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                    />
                    <button
                      type="button"
                      className="dungeon-manager-btn dungeon-manager-btn--small"
                      onClick={() => send({ action: 'rename', dungeonId: selected.id, name: nameDraft })}
                    >
                      Renommer
                    </button>
                  </div>
                  <div className="dungeon-manager-meta">
                    Id : {selected.id} · Phase : {PHASE_LABEL[selected.phase]} · {selected.cellOrWorldDesc.length} cellule(s) ·{' '}
                    {selected.excludedCount} PNJ exclu(s) · {selected.zonesCount} zone(s)
                  </div>
                </div>

                <div className="dungeon-manager-section">
                  <label className="dungeon-manager-section__heading">Timers (minutes)</label>
                  <div className="dungeon-manager-inline">
                    <div className="dungeon-manager-timer-field">
                      <span className="dungeon-manager-timer-field__label">Temps limite</span>
                      <input
                        className="dungeon-manager-input dungeon-manager-input--small"
                        type="number"
                        min="1"
                        value={sessionDraft}
                        onChange={(e) => setSessionDraft(e.target.value)}
                      />
                    </div>
                    <div className="dungeon-manager-timer-field">
                      <span className="dungeon-manager-timer-field__label">Reset après clear/timeout</span>
                      <input
                        className="dungeon-manager-input dungeon-manager-input--small"
                        type="number"
                        min="1"
                        value={resetDraft}
                        onChange={(e) => setResetDraft(e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className="dungeon-manager-btn dungeon-manager-btn--small"
                      onClick={() =>
                        send({
                          action: 'setTimers',
                          dungeonId: selected.id,
                          sessionTimeLimitMinutes: Number(sessionDraft),
                          resetDelayMinutes: Number(resetDraft),
                        })
                      }
                    >
                      Enregistrer
                    </button>
                  </div>
                </div>

                <div className="dungeon-manager-section">
                  <label className="dungeon-manager-section__heading">Tables de loot ({selected.lootContainers.length} coffre(s) configuré(s))</label>
                  {selected.lootContainers.length === 0 && (
                    <div className="dungeon-manager-empty">
                      Aucun coffre configuré - utilise "/dungeonloot {selected.id}" puis vise un coffre en jeu pour en ajouter un.
                    </div>
                  )}
                  <div className="dungeon-manager-loot-list">
                    {selected.lootContainers.map((c) => (
                      <div className="dungeon-manager-loot-row" key={c.refrId}>
                        <span className="dungeon-manager-loot-row__refrid">{c.refrId.toString(16).padStart(8, '0')}</span>
                        <span className="dungeon-manager-loot-row__count">{c.entryCount} entrée(s)</span>
                        <button
                          type="button"
                          className="dungeon-manager-btn dungeon-manager-btn--small"
                          onClick={() => send({ action: 'editLoot', dungeonId: selected.id, refrId: c.refrId })}
                        >
                          Éditer
                        </button>
                        <button
                          type="button"
                          className="dungeon-manager-btn dungeon-manager-btn--small dungeon-manager-btn--danger"
                          onClick={() => send({ action: 'removeLoot', dungeonId: selected.id, refrId: c.refrId })}
                        >
                          Vider
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="dungeon-manager-section dungeon-manager-section--danger-zone">
                  {!confirmingDelete ? (
                    <button
                      type="button"
                      className="dungeon-manager-btn dungeon-manager-btn--danger"
                      onClick={() => setConfirmingDelete(true)}
                    >
                      Supprimer ce donjon
                    </button>
                  ) : (
                    <div className="dungeon-manager-inline">
                      <span className="dungeon-manager-confirm-text">Supprimer "{selected.name}" définitivement ?</span>
                      <button
                        type="button"
                        className="dungeon-manager-btn dungeon-manager-btn--danger"
                        onClick={() => send({ action: 'removeDungeon', dungeonId: selected.id })}
                      >
                        Confirmer
                      </button>
                      <button
                        type="button"
                        className="dungeon-manager-btn dungeon-manager-btn--secondary"
                        onClick={() => setConfirmingDelete(false)}
                      >
                        Annuler
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DungeonManager;
